import {ThunkDispatch, UnknownAction} from '@reduxjs/toolkit';
import {showNotification} from 'platform/components';

import {invalidateOnSuccess, omneticApi} from '@dms/api/base';
import {documentContextApi} from '@dms/api/core';
import {captureAmbiguousApiError, getApiErrorMessages, getApiErrorOrigin} from '@dms/api/utils';

import {ApiError, isDefined} from 'shared/web';

import {
  CloneSaleVehicleV2ApiArg,
  CloneSaleVehicleV2ApiResponse,
  CloneServiceVehicleV2ApiArg,
  CloneServiceVehicleV2ApiResponse,
  CreateRentalVehicleV2ApiArg,
  CreateRentalVehicleV2ApiResponse,
  CreateSaleVehicleV2ApiArg,
  CreateSaleVehicleV2ApiResponse,
  CreateServiceVehicleV2ApiArg,
  CreateServiceVehicleV2ApiResponse,
  FileFieldConfig,
  FileFieldValue,
  SaleVehicleCreateResponseBody,
} from './types';

const VEHICLE_CREATION_TAGS = [
  'AdGeneralSettings',
  'AdPlatformSettings',
  'AdWindshieldSettings',
  'SimilarVehiclesCount',
  'CustomTenantCatalogue',
] as const;

const getSaleVehicleCreationTags = (result: SaleVehicleCreateResponseBody | undefined) =>
  [
    {type: 'Vehicle', id: result?.vehicleId},
    {type: 'SaleVehicle', id: result?.vehicleId},
    {type: 'SaleVehicle', id: `sale_vehicle_from_vehicle_id_${result?.vehicleId}`},
    {type: 'SaleVehicleActions', id: result?.vehicleId},
    {type: 'VehicleLastActiveBC', id: result?.vehicleId},
    ...VEHICLE_CREATION_TAGS,
  ] as const;

type VehicleCreationDispatch = ThunkDispatch<unknown, unknown, UnknownAction>;

const syncVehicleDocuments = async (
  dispatch: VehicleCreationDispatch,
  vehicleId: string,
  fileFields: FileFieldConfig[] | undefined
) => {
  const filesToSync = (fileFields ?? []).filter(
    (field): field is FileFieldConfig & {fieldValue: FileFieldValue} => isDefined(field.fieldValue)
  );

  await Promise.all(
    filesToSync.map((field) =>
      dispatch(
        documentContextApi.endpoints.createDocumentContext.initiate({
          documentContextRequestBody: {
            fileRemoteId: field.fieldValue.fileId,
            contextTarget: 'vehicle',
            contextId: vehicleId,
            fileName: field.fieldValue.fileName,
            note: field.documentNote,
          },
        })
      )
        .unwrap()
        .catch((error: ApiError) => {
          const classification = getApiErrorOrigin(error);

          captureAmbiguousApiError(error, classification);
          showNotification.error(getApiErrorMessages(error), {
            errorOrigin: classification.origin,
          });
        })
    )
  );
};

const syncVehicleDocumentsOnQueryStarted = (
  queryArg: {fileFields?: FileFieldConfig[]},
  lifecycleApi: {
    dispatch: VehicleCreationDispatch;
    queryFulfilled: Promise<{data: {vehicleId: string}}>;
  }
) => {
  lifecycleApi.queryFulfilled
    .then(
      async ({data}) =>
        await syncVehicleDocuments(lifecycleApi.dispatch, data.vehicleId, queryArg.fileFields)
    )
    .catch(() => {
      // Creation failure is handled by the caller — nothing to sync
    });
};

export const vehicleCreationApi = omneticApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (build) => ({
    createSaleVehicleV2: build.mutation<CreateSaleVehicleV2ApiResponse, CreateSaleVehicleV2ApiArg>({
      query: (queryArg) => ({
        url: `/dms/v2/sale-vehicle`,
        method: 'POST',
        body: queryArg.createSaleVehicleV2RequestBody,
        params: {validateMandatoryFields: queryArg.validateMandatoryFields},
      }),
      onQueryStarted: syncVehicleDocumentsOnQueryStarted,
      invalidatesTags: invalidateOnSuccess(getSaleVehicleCreationTags),
    }),
    cloneSaleVehicleV2: build.mutation<CloneSaleVehicleV2ApiResponse, CloneSaleVehicleV2ApiArg>({
      query: (queryArg) => ({
        url: `/dms/v2/sale-vehicle/clone`,
        method: 'POST',
        body: queryArg.cloneSaleVehicleV2RequestBody,
        params: {validateMandatoryFields: queryArg.validateMandatoryFields},
      }),
      onQueryStarted: syncVehicleDocumentsOnQueryStarted,
      invalidatesTags: invalidateOnSuccess(getSaleVehicleCreationTags),
    }),
    createServiceVehicleV2: build.mutation<
      CreateServiceVehicleV2ApiResponse,
      CreateServiceVehicleV2ApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/service-vehicle`,
        method: 'POST',
        body: queryArg.createServiceVehicleV2RequestBody,
        params: {validateMandatoryFields: queryArg.validateMandatoryFields},
      }),
      onQueryStarted: syncVehicleDocumentsOnQueryStarted,
      invalidatesTags: invalidateOnSuccess(
        (result: CreateServiceVehicleV2ApiResponse | undefined) =>
          [{type: 'Vehicle', id: result?.vehicleId}, ...VEHICLE_CREATION_TAGS] as const
      ),
    }),
    cloneServiceVehicleV2: build.mutation<
      CloneServiceVehicleV2ApiResponse,
      CloneServiceVehicleV2ApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/service-vehicle/clone`,
        method: 'POST',
        body: queryArg.cloneServiceVehicleV2RequestBody,
        params: {validateMandatoryFields: queryArg.validateMandatoryFields},
      }),
      onQueryStarted: syncVehicleDocumentsOnQueryStarted,
      invalidatesTags: invalidateOnSuccess(
        (result: CloneServiceVehicleV2ApiResponse | undefined) =>
          [{type: 'Vehicle', id: result?.vehicleId}, ...VEHICLE_CREATION_TAGS] as const
      ),
    }),
    createRentalVehicleV2: build.mutation<
      CreateRentalVehicleV2ApiResponse,
      CreateRentalVehicleV2ApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/rental-vehicle`,
        method: 'POST',
        body: queryArg.createRentalVehicleV2RequestBody,
      }),
      onQueryStarted: syncVehicleDocumentsOnQueryStarted,
      invalidatesTags: invalidateOnSuccess(
        (result: CreateRentalVehicleV2ApiResponse | undefined) =>
          [
            {type: 'Vehicle', id: result?.vehicleId},
            {type: 'RentalVehicle', id: result?.vehicleId},
            {type: 'RentalVehicle', id: 'CALENDAR'},
            ...VEHICLE_CREATION_TAGS,
          ] as const
      ),
    }),
  }),
});

export const {
  useCreateSaleVehicleV2Mutation,
  useCloneSaleVehicleV2Mutation,
  useCreateServiceVehicleV2Mutation,
  useCloneServiceVehicleV2Mutation,
  useCreateRentalVehicleV2Mutation,
} = vehicleCreationApi;
